// Inventory Counting Dashboard Service - Manager view for counting overview
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// ==================== Interfaces ====================

export interface CountingSummary {
  itemNo: string;
  description1: string;
  warehouse: string;
  stdLocationName?: string;
  operatorID?: string;
  operatorName?: string;
  quantityCounted: number;
  quantityExpected: number;
  hasDeviation: boolean;
  deviationQuantity: number;
  deviationValue: number;
  isCorrected: boolean;
  purchasePrice?: number;
  currency?: string;
  freeTxt1?: string;
  freeTxt2?: string;
  freeTxt3?: string;
  locationDetails?: string;
  transactionCount: number;
  latestTransDt?: Date;
}

export interface CountingDetail {
  lineID: number;
  batchID: string;
  itemNo: string;
  description1?: string;
  locationName?: string;
  operatorID: string;
  operatorName?: string;
  transDt?: Date;
  quantityOriginal?: number;
  quantityCorrected?: number;
  warehouse?: string;
  lineStatus: number;
  lineType: number;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  grShipmentNo?: number;
  price?: number;
  currency?: string;
}

export interface CountingBinStatus {
  binCode: string;
  quantity?: number;
  grShipmentNo?: number;
  lastCountedDate?: Date;
  lastMovementDate?: Date;
  lastReplenishDate?: Date;
  resQuantity?: number;
  availableQuantity?: number;
  itemNo: string;
  warehouseID: string;
}

export interface WarehouseInfo {
  id: number;
  companyNo: string;
  warehouseID: string;
  warehouseDescr: string;
  externalID?: string;
  useAWYesNo: boolean;
  useLocationYesNo: boolean;
  /**
   * Indicates if the connected ERP system is an Advanced Warehouse (location-aware) system.
   * When true: Export detailed counting lines per item + location to ERP.
   * When false: Export aggregated counting per item (sum of all locations).
   */
  erpAWYesNo: boolean;
  /**
   * When enabled, the warehouse is in "stock counting mode".
   * This overrides Profile.CloseSCOnDeviceYesNo - all counts go to manager validation.
   */
  inStockCountingMode: boolean;
}

export interface CountingDashboardData {
  warehouse?: WarehouseInfo;
  summary: CountingSummary[];
  totalItems: number;
  itemsWithDeviation: number;
  totalDeviationValue: number;
  correctedItems: number;
}

export interface CountingStatistics {
  totalCountingBatches: number;
  inProgressBatches: number;
  completedBatches: number;
  totalItemsCounted: number;
  itemsWithDeviation: number;
  deviationPercentage: number;
  totalDeviationValue: number;
}

export interface ExportedFile {
  filePath: string;
  fileName: string;
  exportType: number;
  orderType: number;
  lineCount: number;
  fileSize: number;
}

export interface ExportCountingResponse {
  success: boolean;
  errorMessage?: string;
  exportedFiles: ExportedFile[];
  itemsExported: number;
  transactionsExported: number;
  exportedAt: Date;
  itemBinsUpdated: boolean;
}

// ==================== Service Class ====================

class InventoryCountingDashboardService {
  private baseUrl = `${API_BASE_URL}/inventory-counting-dashboard`;

  /**
   * Get complete counting dashboard data for a warehouse
   */
  async getCountingDashboard(
    warehouseId: string,
    operatorId?: string,
    deviationOnly?: boolean,
    itemNo?: string
  ): Promise<CountingDashboardData> {
    try {
      const params = new URLSearchParams({
        warehouseId,
        ...(operatorId && { operatorId }),
        ...(deviationOnly !== undefined && { deviationOnly: String(deviationOnly) }),
        ...(itemNo && { itemNo })
      });

      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/dashboard?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch counting dashboard: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching counting dashboard:', error);
      throw error;
    }
  }

  /**
   * Get aggregated counting summary per item for a warehouse
   */
  async getCountingSummary(
    warehouseId: string,
    operatorId?: string,
    deviationOnly?: boolean,
    itemNo?: string
  ): Promise<CountingSummary[]> {
    try {
      const params = new URLSearchParams({
        warehouseId,
        ...(operatorId && { operatorId }),
        ...(deviationOnly !== undefined && { deviationOnly: String(deviationOnly) }),
        ...(itemNo && { itemNo })
      });

      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/summary?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch counting summary: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching counting summary:', error);
      throw error;
    }
  }

  /**
   * Get detailed counting transactions for a specific item
   */
  async getCountingDetails(
    warehouseId: string,
    itemNo: string
  ): Promise<CountingDetail[]> {
    try {
      const params = new URLSearchParams({
        warehouseId,
        itemNo
      });

      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/details?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch counting details: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching counting details:', error);
      throw error;
    }
  }

  /**
   * Get bin-level inventory status for a specific item (AW warehouses only)
   */
  async getCountingBinStatus(
    warehouseId: string,
    itemNo: string
  ): Promise<CountingBinStatus[]> {
    try {
      const params = new URLSearchParams({
        warehouseId,
        itemNo
      });

      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/bin-status?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch counting bin status: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching counting bin status:', error);
      throw error;
    }
  }

  /**
   * Update corrected quantity for a counting transaction (inline editing)
   */
  async updateCountingCorrection(
    batchId: string,
    lineId: number,
    quantityCorrected: number,
    updatedBy?: string
  ): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedPatch(
        `${this.baseUrl}/${batchId}/lines/${lineId}/correction`,
        {
          quantityCorrected,
          updatedBy
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update counting correction: ${response.statusText}`);
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error updating counting correction:', error);
      throw error;
    }
  }

  /**
   * Export and finalize counting data for selected items
   * Automatically detects if warehouse is AW or Standard
   */
  async exportCounting(
    warehouseId: string,
    itemNumbers: string[],
    exportedBy?: string
  ): Promise<ExportCountingResponse> {
    try {
      const response = await AuthService.authenticatedPost(
        `${this.baseUrl}/export`,
        {
          warehouseID: warehouseId,
          itemNumbers,
          exportedBy
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errorMessage || 'Failed to export counting data');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error exporting counting data:', error);
      throw error;
    }
  }

  /**
   * Get counting statistics for dashboard overview
   */
  async getCountingStatistics(): Promise<CountingStatistics> {
    try {
      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/statistics`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch counting statistics: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching counting statistics:', error);
      throw error;
    }
  }

  /**
   * Check if a warehouse uses Advanced Warehouse (AW) mode
   */
  async isAdvancedWarehouse(warehouseId: string): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/warehouse/${warehouseId}/is-aw`
      );

      if (!response.ok) {
        throw new Error(`Failed to check warehouse type: ${response.statusText}`);
      }

      const data = await response.json();
      return data.isAW;
    } catch (error) {
      console.error('Error checking warehouse type:', error);
      throw error;
    }
  }
}

export default new InventoryCountingDashboardService();
